"use client";

import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { readBrandCookieState } from "./brand-cookie";
import { sameBrand } from "./brand.types";
import type { Brand } from "./brand.types";

interface BrandContextType {
	/** null means INFINIT — the unbranded default. */
	brand: Brand | null;
	/**
	 * True only while a resolver is in flight and the answer is not yet known.
	 * Surfaces render a neutral placeholder rather than the INFINIT mark while it
	 * holds, so a branded organisation never sees INFINIT flash.
	 *
	 * Critically it is false when nothing is resolving — several routes mount no
	 * resolver at all, and they must render INFINIT rather than wait forever.
	 */
	isResolving: boolean;
	setResolvedBrand: (brand: Brand | null) => void;
	/** Called by a resolver while it is in flight. Returns its deregister fn. */
	registerResolver: () => () => void;
}

const FALLBACK: BrandContextType = {
	brand: null,
	isResolving: false,
	setResolvedBrand: () => {},
	registerResolver: () => () => {},
};

const BrandContext = createContext<BrandContextType | undefined>(undefined);

export function BrandProvider({ children }: { children: React.ReactNode }) {
	const [brand, setBrand] = useState<Brand | null>(null);
	// Whether the answer is known — from the cookie, or from a completed resolve.
	const [isKnown, setIsKnown] = useState(false);
	const [activeResolvers, setActiveResolvers] = useState(0);

	// Read in an effect, not a lazy initialiser: the prerendered markup cannot
	// know the cookie, so deriving it during the first render would risk a
	// hydration mismatch. The pre-paint script has already applied the tab title
	// and favicon, which are the only things that would visibly flicker.
	useEffect(() => {
		const { known, brand: fromCookie } = readBrandCookieState();
		if (known) {
			setBrand(fromCookie);
			setIsKnown(true);
		}
	}, []);

	const setResolvedBrand = useCallback((resolved: Brand | null) => {
		// Keeping the current object when nothing changed avoids a needless
		// re-render; the comparison covers every field, so re-pointed artwork is
		// still picked up rather than pinned to whatever the cookie last held.
		setBrand(current => (sameBrand(current, resolved) ? current : resolved));
		setIsKnown(true);
	}, []);

	const registerResolver = useCallback(() => {
		setActiveResolvers(n => n + 1);
		return () => setActiveResolvers(n => Math.max(0, n - 1));
	}, []);

	const value = useMemo<BrandContextType>(
		() => ({
			brand,
			isResolving: !isKnown && activeResolvers > 0,
			setResolvedBrand,
			registerResolver,
		}),
		[brand, isKnown, activeResolvers, setResolvedBrand, registerResolver]
	);

	return (
		<BrandContext.Provider value={value}>{children}</BrandContext.Provider>
	);
}

/**
 * Safe outside a BrandProvider — unbranded surfaces render INFINIT as they
 * always have.
 */
export function useBrand(): BrandContextType {
	return useContext(BrandContext) ?? FALLBACK;
}
